import {Card, CardContent, Typography} from '@material-ui/core';
import {SvgIconProps} from '@material-ui/core/SvgIcon';
import {getNews} from 'Agent/AgentInformation/ducks/news';
import 'Core/components/Dashboard/AgentNewsCard.sass';
import 'Core/components/Dashboard/Card.sass';
import {matchAsyncPage} from 'Core/models/Async';
import {flow} from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import Loading from 'Shared/components/Loading';
import {curry} from 'Shared/utilities/curry';

type AgentNewsCardProps = {
  image: React.ComponentType<SvgIconProps> | React.FunctionComponent<any>;
  linkText: string;
  onClickLink: () => void;
};

const getNextNewsItemIndex = (newsItems: string[], currentNewsItem: number) =>
  currentNewsItem < newsItems.length - 1 ? currentNewsItem + 1 : 0;

const AgentNewsCard = (props: AgentNewsCardProps) => {
  const dispatch = useDispatch();
  const news = useSelector((state: ReducerState) => state.news);
  const unableToLoadNewsMessage = <span>Unable to load news.</span>;

  useEffect(() => {
    dispatch(getNews());
  }, []);

  return (
    <Card className="dashboard-card dashboard__fadeInRight ">
      <div className="dashboard__fade">
        <div className="dashboard-card__icon">
          <props.image className="dashboard-card__icon" />
        </div>
        <div className="dashboard-card__header">
          <Typography className="dashboard-card__title" variant="body1" component="h2">
            Agent News
          </Typography>
        </div>
        <CardContent className="dashboard-card__description">
          {matchAsyncPage(news, {
            Conflict: () => unableToLoadNewsMessage,
            ServerError: () => unableToLoadNewsMessage,
            Unauthorized: () => unableToLoadNewsMessage,
            Loading: () => <Loading />,
            Loaded: loadedNews => <AgentNewsCarousel currentNews={loadedNews.result.map(news => news.title!)} />,
          })}
        </CardContent>
        <div className="dashboard-card__actions">
          <Typography onClick={props.onClickLink} className="dashboard-card__link" variant="caption">
            {props.linkText}
            <i className="fa fa-chevron-right dashboard-card__link-icon" aria-hidden="true" />
          </Typography>
        </div>
      </div>
    </Card>
  );
};

const AgentNewsCarousel = (props: {currentNews: string[]}) => {
  const {currentNews} = props;
  const [currentCarouselItemNumber, setCarouselItemNumber] = useState(0);
  const [carouselTimerId, setCarouselTimerId] = useState<O.Option<number>>(O.none);

  const FIVE_SECONDS = 5 * 1000;
  const nextNewsItemIndex = curry(getNextNewsItemIndex)(currentNews);
  const advanceCarousel = flow(nextNewsItemIndex, setCarouselItemNumber);

  useEffect(() => {
    const newTimerId = window.setTimeout(() => advanceCarousel(currentCarouselItemNumber), FIVE_SECONDS);
    setCarouselTimerId(O.some(newTimerId));

    return () => {
      pipe(
        O.some((timerId: number) => clearInterval(timerId)),
        O.ap(carouselTimerId)
      );
    };
  }, [currentCarouselItemNumber]);

  return (
    <div>
      {currentCarouselItemNumber % 2 ? (
        <Typography className={'fade-in-even'} variant="caption" component="div" style={{opacity: 0}}>
          {currentNews[currentCarouselItemNumber]}
        </Typography>
      ) : (
        <Typography className={'fade-in'} variant="caption" component="p" style={{opacity: 0}}>
          {currentNews[currentCarouselItemNumber]}
        </Typography>
      )}
    </div>
  );
};

export default AgentNewsCard;
